import { Component, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';

import { PortalApiService } from '../../portal-api.service';
import { PortalAuthService } from '../../portal-auth.service';
import { PortalPreviewService } from '../../portal-preview.service';
import { visibleSections } from '../portal-sections';

/**
 * The bar across the top of a staff preview: that it is a preview, and which role it is being seen
 * through.
 *
 * The role switcher lives here, inside the preview, rather than in WeQuote's chrome around it —
 * comparing two roles on the same screen is the whole point, and that reads better as part of the
 * thing being previewed. Project is not offered here: the portal's own projects list already is that
 * choice, so a second control would be a duplicate.
 *
 * What switching does NOT do is re-key the session: PortalPreviewService asks WeQuote for a fresh
 * one, because only WeQuote holds the staff session that may decide scope.
 */
@Component({
	selector: 'portal-preview-banner',
	templateUrl: './portal-preview-banner.component.html',
	styleUrls: ['./portal-preview-banner.component.scss'],
	encapsulation: ViewEncapsulation.None
})
export class PortalPreviewBannerComponent {

	note = '';

	constructor(
		public preview: PortalPreviewService,
		private api: PortalApiService,
		private auth: PortalAuthService,
		private router: Router
	) {}

	get roleId(): number | null {
		return this.preview.current?.role_id ?? null;
	}

	get projectId(): number | null {
		return this.preview.current?.project_id ?? null;
	}

	// ngValue hands back the id itself (or null for Unrestricted), not a string.
	onRoleChange(roleId: number | null): void {
		if (roleId === this.roleId) return;

		const projectId = this.projectId;
		if (!projectId) return;

		this.apply({ project_id: projectId, role_id: roleId });
	}

	private apply(scope: { project_id: number; role_id: number | null }): void {
		this.note = '';

		this.preview.switchTo(scope).then(ok => {
			if (!ok) {
				this.note = this.preview.failureMessage || 'Could not switch the preview.';
				return;
			}

			this.auth.ensureSession(true);
			this.settle(scope.project_id);
		});
	}

	/**
	 * Show the page again through the new role, staying where they are.
	 *
	 * Changing role must never change project — the useful comparison is the same screen seen two
	 * ways. The single exception is a section the new role can't see, which has nowhere to stay and
	 * falls back to the project's own page.
	 */
	private settle(projectId: number): void {
		const section = this.currentSection();

		// The projects list shows nothing that depends on the role, so leave it completely alone.
		// Reloading it here would also re-enter it as a preview that now *has* a project, which the
		// list forwards straight into — a role change would look like a jump into a project.
		if (this.onProjectList()) return;

		// The project's own page: its section tiles follow the role, but no permission can be lost.
		if (!section) { this.refresh(); return; }

		this.api.project.get(projectId, (res: any) => {
			if (visibleSections(res?.sections || []).includes(section)) { this.refresh(); return; }

			// Gone for this role. A real navigation, so the page fetches for itself — and the banner
			// survives it (it lives outside the router outlet), which is how the note stays visible.
			this.note = `This role can’t see ${this.sectionLabel(section)}.`;
			this.router.navigate(['/project', projectId]);
		}, () => this.refresh());
	}

	private onProjectList(): boolean {
		return this.pathParts()[0] !== 'project';
	}

	/**
	 * Re-render the current page against the new role.
	 *
	 * A reload rather than a router navigation: the URL hasn't changed, and the router ignores a
	 * navigation to the URL it is already on — which would leave the previous role's data on screen.
	 * Reloading re-runs the handshake, and WeQuote mints against the role just chosen.
	 */
	private refresh(): void {
		window.location.reload();
	}

	// The section segment of the current URL, e.g. 'invoices' from /project/12/invoices.
	private currentSection(): string | null {
		const parts = this.pathParts();
		if (parts[0] !== 'project' || parts.length < 3) return null;

		// /project/:id/document/:quoteId belongs to the documents section.
		return parts[2] === 'document' ? 'documents' : parts[2];
	}

	private pathParts(): string[] {
		return this.router.url.split('?')[0].split('#')[0].split('/').filter(p => p !== '');
	}

	private sectionLabel(key: string): string {
		return key.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
	}
}
